import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import {
  DEFAULT_LOGISTIC_PROVIDER_VALUES,
  LogisticProviderForm,
  NewLogisticProviderForm,
  logisticProviderSchema,
} from '@lp/features/admin/forms/LogisticProviderForm';
import { AuthContext } from '@lp/core/auth';
import { CoverageEntry, LogisticProvider } from '@/api/apis/facilities';
import { useRemoveTXCenterProviderMutation, useUpdateTXCenterProviderMutation } from '@/queries/txcenter-providers';
import { useRemovePartnerProviderMutation, useUpdatePartnerProviderMutation } from '@/queries/partner-providers';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { convertToIanaTimezone, getChangedValues, getShortTzByIanaName } from '@/utils/helpers';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';

type LogisticProvidersEditProps = {
  type: string;
};

type LogisticProviderApiForm = Omit<NewLogisticProviderForm, 'coverage'> & { coverage: CoverageEntry[] };

export const LogisticProvidersEdit = ({ type }: LogisticProvidersEditProps) => {
  const navigate = useNavigate();
  const data = useLoaderData<LogisticProvider>();
  const { mutateAsync: mutateAsyncTXCenter } = useUpdateTXCenterProviderMutation();
  const { mutateAsync: mutateAsyncPartner } = useUpdatePartnerProviderMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.ProvidersConfigurationUpdate);

  const defaultValues = useMemo<NewLogisticProviderForm>(() => {
    const coverage = Object.fromEntries(
      (data.coverage ?? []).map(({ country, subdivisions }) => [country, subdivisions]),
    );
    return {
      ...DEFAULT_LOGISTIC_PROVIDER_VALUES,
      logoPath: data.logoPath ?? undefined,
      name: data.name,
      email: data.email,
      primaryPhone: data.primaryPhone,
      secondaryPhone: data.secondaryPhone || '',
      aircraftType: data.aircraftType || '',
      coverage,
      country: data.country,
      state: data.state,
      addressLine: data.addressLine,
      city: data.city,
      postalCode: data.postalCode,
      timezone: getShortTzByIanaName(data.timezone || '') || '',
      timezoneIANA: data.timezone || '',
    };
  }, [data]);

  const isPartnerProvider = type === 'Partner';
  const title = `Edit ${isPartnerProvider ? 'Supplemental Lift' : 'TX Center'} Provider`;
  const backToListLink = `/admin/logistics_providers/${isPartnerProvider ? 'supplemental_lift' : 'tx_center'}`;
  const deleteModalTitle = `Delete ${isPartnerProvider ? 'Supplemental Lift' : 'TX Center'} Provider?`;
  const deleteModalBody = `Are you sure you want to delete the ${isPartnerProvider ? 'Supplemental Lift' : 'TX Center'} Provider?`;

  const handleBackToList = () => {
    navigate(backToListLink);
  };

  const handleSubmit = async (values: NewLogisticProviderForm) => {
    try {
      let uploadedFile;

      if (values.image) {
        uploadedFile = await uploadFiles([values.image]);
      }

      const updateBody = {
        name: values.name,
        email: values.email,
        primaryPhone: values.primaryPhone,
        secondaryPhone: values.secondaryPhone,
        aircraftType: values.aircraftType || null,
        coverage: Object.entries(values.coverage).map(([country, subdivisions]) => ({ country, subdivisions })),
        country: values.country || null,
        state: values.state || null,
        addressLine: values.addressLine || null,
        city: values.city || null,
        postalCode: values.postalCode || null,
        timezone: values.timezoneIANA || convertToIanaTimezone(values.timezone),
        logoPath: uploadedFile ? uploadedFile[0].fileName : values.logoPath,
      };

      const changedValues = getChangedValues<Partial<LogisticProviderApiForm>>(updateBody, {
        ...defaultValues,
        coverage: data.coverage ?? [],
      });

      const body = {
        id: data.id,
        ...changedValues,
      };

      if (isPartnerProvider) {
        await mutateAsyncPartner(body);
      } else {
        await mutateAsyncTXCenter(body);
      }

      toast.success(`Logistics Provider updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: isPartnerProvider ? useRemovePartnerProviderMutation : useRemoveTXCenterProviderMutation,
    successMessage: 'Logistics Provider deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleLogisticProviderDelete = () => {
    onModalOpen(data.id);
  };

  return (
    <>
      <Form<NewLogisticProviderForm>
        defaultValues={defaultValues}
        schema={logisticProviderSchema}
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-9">
          <AdminPageHeader title={title} backToListLink={backToListLink}>
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleLogisticProviderDelete}
              hasDeletePermissions={hasCreatePermissions}
              hasEditPermissions={hasCreatePermissions || hasEditPermissions}
            />
          </AdminPageHeader>
          <LogisticProviderForm
            isPartnerProvider={isPartnerProvider}
            isReadOnly={!hasCreatePermissions && !hasEditPermissions}
          />
        </div>
      </Form>
      <DeleteModal
        title={deleteModalTitle}
        description={deleteModalBody}
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
